const SENSITIVE_FIELDS = new Set([
  'tokenhash', 'senha', 'password', 'xml', 'pdf', 'xmlvenda', 'xmlcancelamento',
  'escpos', 'refreshtoken', 'accesstoken', 'clientsecret', 'internalapikey',
  'jwt', 'chave', 'certificado', 'retornojson',
]);

/** Campos de sistema que mudam sozinhos e não interessam na auditoria. */
const NOISE_FIELDS = new Set([
  'dataupdate',
  'datacadastro',
  'datacriacao',
  'ultimocontatoem',
  'datahora',
  'datahorautc',
]);

const FIELD_LABELS = {
  descricao: 'Descrição',
  nome: 'Nome',
  nomefantasia: 'Nome fantasia',
  razaosocial: 'Razão social',
  valor: 'Valor',
  valortotal: 'Valor total',
  valorunitario: 'Valor unitário',
  preco: 'Preço',
  precovenda: 'Preço venda',
  precoatacado: 'Preço atacado',
  quantidade: 'Quantidade',
  ativo: 'Ativo',
  cancelada: 'Cancelada',
  motivocancelamento: 'Motivo cancelamento',
  datacancelamento: 'Data cancelamento',
  datavenda: 'Data venda',
  email: 'E-mail',
  telefone: 'Telefone',
  documento: 'Documento/CNPJ',
  cnpj: 'CNPJ',
  cpf: 'CPF',
  codigo: 'Código',
  estoque: 'Estoque',
  estoqueminimo: 'Estoque mínimo',
  categoriaid: 'Categoria',
  subcategoriaid: 'Subcategoria',
  marcaid: 'Marca',
  grupotributarioid: 'Grupo tributário',
  clienteid: 'Cliente',
  fornecedorid: 'Fornecedor',
  produtoid: 'Produto',
  observacao: 'Observação',
  tipovenda: 'Tipo venda',
  desconto: 'Desconto',
  acrescimo: 'Acréscimo',
  revogado: 'Revogado',
  expiraem: 'Expira em',
  status: 'Status',
  statusfiscal: 'Status fiscal',
  imprimecupompresente: 'Cupom presente',
  perguntaseimprime: 'Perguntar se imprime',
  solicitawhatsappvenda: 'WhatsApp na venda',
  solicitacadastroresumido: 'Cadastro resumido',
};

const money = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export function labelCampo(campo) {
  const key = String(campo || '').toLowerCase();
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return String(campo || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/Id$/, ' ID')
    .trim();
}

export function campoIgnorado(campo) {
  const key = String(campo || '').toLowerCase();
  return SENSITIVE_FIELDS.has(key) || NOISE_FIELDS.has(key);
}

export function formatValorCampo(campo, valor) {
  if (valor === null || valor === undefined || valor === '') return '—';
  const key = String(campo || '').toLowerCase();
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
  if (key.includes('data') || key.endsWith('em') || key.endsWith('utc')) {
    const d = new Date(valor);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString('pt-BR');
  }
  if (/(valor|preco|total|desconto|acrescimo|troco)/i.test(key)) {
    const m = money(valor);
    if (m) return m;
  }
  if (typeof valor === 'object') return JSON.stringify(valor);
  return String(valor);
}

export function parseAuditJson(raw) {
  if (!raw) return {};
  try {
    const obj = JSON.parse(raw);
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

function camposVisiveis(obj) {
  return Object.entries(obj || {}).filter(([k]) => !campoIgnorado(k));
}

export function montarAlteracoes(item) {
  const antes = parseAuditJson(item.valoresAntes);
  const depois = parseAuditJson(item.valoresDepois);
  const acao = (item.acao || '').toUpperCase();

  if (acao === 'UPDATE') {
    const chaves = [...new Set([...Object.keys(antes), ...Object.keys(depois)])]
      .filter((k) => !campoIgnorado(k));
    return chaves.map((campo) => ({
      campo,
      label: labelCampo(campo),
      antes: formatValorCampo(campo, antes[campo]),
      depois: formatValorCampo(campo, depois[campo]),
    })).filter((r) => r.antes !== r.depois);
  }

  const fonte = acao === 'DELETE' ? antes : depois;
  return camposVisiveis(fonte).map(([campo, valor]) => ({
    campo,
    label: labelCampo(campo),
    antes: acao === 'DELETE' ? formatValorCampo(campo, valor) : null,
    depois: acao === 'DELETE' ? null : formatValorCampo(campo, valor),
  }));
}

/** Lista de nomes de campos alterados, separados por vírgula. */
export function montarCamposLista(item) {
  const alteracoes = montarAlteracoes(item);
  if (alteracoes.length === 0) return '—';
  return alteracoes.map((a) => a.label).join(', ');
}

export function montarResumo(item) {
  const alteracoes = montarAlteracoes(item);
  if (alteracoes.length === 0) return 'Sem detalhes visíveis';
  const partes = alteracoes.slice(0, 3).map((a) => {
    if (a.antes != null && a.depois != null) return `${a.label}: ${a.antes} → ${a.depois}`;
    if (a.depois != null) return `${a.label}: ${a.depois}`;
    return `${a.label}: ${a.antes}`;
  });
  const extra = alteracoes.length > 3 ? ` (+${alteracoes.length - 3})` : '';
  return partes.join(' · ') + extra;
}

export function nomeResponsavel(item) {
  const nome = (item?.usuarioNome || '').trim();
  if (nome) return nome;
  if (item?.usuarioId) return `Usuário #${item.usuarioId}`;
  return '—';
}
